import React, { useState, useEffect, useRef } from 'react';
import { 
  AppLayout, 
  SideNavigation, 
  TopNavigation,
  Container,
  Header,
  SpaceBetween,
  Button,
  Alert,
  Toggle,
  Modal,
  Box
} from '@cloudscape-design/components';
import '@cloudscape-design/global-styles/index.css';
import { applyMode, Mode } from '@cloudscape-design/global-styles';
import axios from 'axios';

// 导入组件
import TaskView from './components/TaskView';
import TaskManager from './components/TaskManager';
import PointsSystem from './components/PointsSystem';
import FamilySchedule from './components/FamilySchedule';
import FamilySettingsView from './components/TestView';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';


// API配置
import { API_BASE_URL } from './config/api';
// 身份验证工具
import { autoLogin, logout, getCurrentUser, setAuthToken, setOnAuthFailed } from './utils/auth';

function App() {
  const [activeHref, setActiveHref] = useState('/tasks');
  const [currentUser, setCurrentUser] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [familyName, setFamilyName] = useState(''); // 添加家庭名称状态
  const [chatrooms, setChatrooms] = useState([]); // 添加班级群聊天室信息

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [visible, setVisible] = useState(false); // 测试 Modal 的状态
  
  // 防止重复初始化
  const hasInitialized = useRef(false);

  // 初始化数据 - 防止 Strict Mode 重复执行
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      
      // 设置认证失败回调
      setOnAuthFailed((errorInfo) => {
        console.log('🔴 认证失败回调触发:', errorInfo);
        setIsAuthenticated(false);
        setAuthError(errorInfo.error || '认证已失效,请重新登录');
        setCurrentUser(null);
        setFamilyMembers([]);
        setLoading(false);
      });
      
      initializeApp();
    }
  }, []);

  // 初始化暗黑模式
  useEffect(() => {
    // 从本地存储读取用户偏好
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);
    applyMode(savedDarkMode ? Mode.Dark : Mode.Light);
  }, []);

  // 切换暗黑模式
  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    applyMode(newDarkMode ? Mode.Dark : Mode.Light);
    localStorage.setItem('darkMode', newDarkMode.toString());
  };

  const toggleTest = () => { 
    setVisible(true);
  };

  const initializeApp = async () => {
    try {
      setLoading(true);
      setAuthError(null);
      setError(null);
      
      // 首先进行身份验证
      const authResult = await autoLogin();
      
      if (!authResult.success) {
        setIsAuthenticated(false);
        setAuthError(authResult.error || '身份验证失败');
        console.error('❌ 身份验证失败:', authResult.error);
        setError('身份验证失败，请检查网络连接或联系管理员');
        return;
      }
      
      setIsAuthenticated(true);
      
      // 1. 先加载当前用户（从 localStorage）
      const user = await loadCurrentUser();
      
      // 2. 只请求一次家庭成员信息
      if (user && user.family_id) {
        await loadFamilyMembers(user.family_id);
      } else {
        console.error('❌ 无法获取用户的 familyId');
      }
    } catch (error) {
      console.error('初始化应用失败:', error);
      setError('应用初始化失败，请刷新页面重试');
    } finally {
      setLoading(false);
    }
  };

  // 加载家庭成员 - 使用当前用户的 familyId
  const loadFamilyMembers = async (familyId) => {
    if (!familyId) {
      console.error('❌ App.js: 缺少 familyId，无法加载家庭成员');
      return;
    }
    
    try {
      const response = await axios.get(`${API_BASE_URL}/families/${familyId}/info`);
      setFamilyMembers(response.data.members || []);
      // 存储家庭名称
      setFamilyName(response.data.name || '');
      // 存储班级群聊天室信息
      setChatrooms(response.data.chatrooms || []);
    } catch (error) {
      console.error('加载家庭成员失败:', error);
      // 401错误会被拦截器处理,这里只处理其他错误
      if (error.response && error.response.status !== 401) {
        setError('加载家庭信息失败，请稍后重试');
      }
    }
  };



  // 加载当前用户
  const loadCurrentUser = async () => {
    try {
      // 从localStorage获取当前用户信息
      const user = getCurrentUser();
      if (user) {
        setCurrentUser(user);
        // 同时保存 familyId 到 localStorage
        if (user.family_id) {
          localStorage.setItem('family_id', user.family_id);
        }
        return user;
      } else {
        // 如果没有保存的用户信息，使用默认用户
        const defaultUser = {
          id: '1',
          name: '管理员',
          role: 'parent',
          family_id: '6869d0771dfa97eaefc3bbf7',
          timeZone: 'Asia/Shanghai'
        };
        setCurrentUser(defaultUser);
        localStorage.setItem('family_id', defaultUser.family_id);
        return defaultUser;
      }
    } catch (error) {
      console.error('加载当前用户失败:', error);
      return null;
    }
  };

  // 创建任务
  const handleCreateTask = async (taskData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/tasks`, {
        ...taskData,
        status: 'open',
        points: taskData.points || 0,
        family_id: currentUser?.family_id || '6869d0771dfa97eaefc3bbf7',
        creator_id: currentUser?.id || '1'
      });
    } catch (error) {
      console.error('创建任务失败:', error);
      throw error;
    }
  };

  // 更新任务
  const handleUpdateTask = async (taskId, updates) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/tasks/${taskId}`, updates);
    } catch (error) {
      console.error('更新任务失败:', error);
      throw error;
    }
  };

  // 删除任务
  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API_BASE_URL}/tasks/${taskId}`);
    } catch (error) {
      console.error('删除任务失败:', error);
      throw error;
    }
  };

  // 完成任务
  const handleCompleteTask = async (taskId) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/tasks/${taskId}/complete`, currentUser?.id);
    } catch (error) {
      console.error('完成任务失败:', error);
      throw error;
    }
  };

  // 编辑任务
  const handleEditTask = async (taskId, taskData) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/tasks/${taskId}`, taskData);
    } catch (error) {
      console.error('编辑任务失败:', error);
      throw error;
    }
  };

  // 更新用户信息
  const handleUpdateUser = async (userId, updates) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/family-members/${userId}/`, updates);
      setFamilyMembers(prev => prev.map(member => 
        member._id === userId ? response.data : member
      ));
      
      if (currentUser && currentUser._id === userId) {
        setCurrentUser(response.data);
      }
    } catch (error) {
      console.error('更新用户信息失败:', error);
      throw error;
    }
  };

  // 添加家庭成员
  const handleAddFamilyMember = async (memberData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/family-members/`, memberData);
      setFamilyMembers(prev => [...prev, response.data]);
    } catch (error) {
      console.error('添加家庭成员失败:', error);
      throw error;
    }
  };

  // 更新家庭成员
  const handleUpdateFamilyMember = async (memberId, updates) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/family-members/${memberId}/`, updates);
      setFamilyMembers(prev => prev.map(member => 
        member._id === memberId ? response.data : member
      ));
    } catch (error) {
      console.error('更新家庭成员失败:', error);
      throw error;
    }
  };

  // 金币兑换
  const handleExchangePoints = async (exchangeData) => {
    try {
      await axios.post(`${API_BASE_URL}/exchange-points/`, exchangeData);
      // 重新加载用户数据
      await loadCurrentUser();
    } catch (error) {
      console.error('金币兑换失败:', error);
    }
  };

  // 家庭创建成功后的处理
  const handleFamilyCreated = async (familyData) => {
    try {
      // 更新当前用户的家庭信息
      const updatedUser = {
        ...currentUser,
        family_id: familyData.id
      };
      setCurrentUser(updatedUser);
      
      // 刷新用户信息和家庭成员信息
      await loadCurrentUser();
      await loadFamilyMembers();
      
    } catch (error) {
      console.error('处理家庭创建后逻辑失败:', error);
    }
  };

  // 渲染主要内容
  const renderContent = () => {
    if (loading) {
      return (
        <Container>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            加载中...
          </div>
        </Container>
      );
    }

    if (!isAuthenticated || authError) {
      return (
        <Container>
          {authError && (
            <Alert 
              type="warning" 
              header="需要重新登录"
              dismissible
              onDismiss={() => setAuthError(null)}
            >
              {authError}
            </Alert>
          )}
          <LoginForm
            onLoginSuccess={(result) => {
              // 保存token与用户信息
              if (result?.token) {
                setAuthToken(result.token);
              }
              if (result?.user) {
                try { localStorage.setItem('current_user', JSON.stringify(result.user)); } catch {}
              }
              setIsAuthenticated(true);
              setAuthError(null);
              // 登录成功后重新加载数据
              initializeApp();
            }}
            onLoginFailure={(result) => {
              setAuthError(result.error || '登录失败');
            }}
          />
        </Container>
      );
    }

    if (error) {
      return (
        <Container>
          <SpaceBetween size="m">
            <Alert type="error" header="错误">
              {error}
            </Alert>
            <Button onClick={initializeApp}>重试</Button>
          </SpaceBetween>
        </Container>
      );
    }
  };

  return (
    <div style={{ minHeight: '100vh' }}>
      <TopNavigation
        identity={{
          href: '/',
          title: '家庭任务管理系统'
        }}
        utilities={[
          {
            type: 'button',
            text: '任务视图',
            variant: activeHref === '/tasks' ? 'primary-button' : 'link',
            onClick: () => setActiveHref('/tasks')
          },
          {
            type: 'button',
            text: '任务管理',
            variant: activeHref === '/task-manager' ? 'primary-button' : 'link',
            onClick: () => setActiveHref('/task-manager')
          },
          {
            type: 'button',
            text: '奖惩制度',
            variant: activeHref === '/points-system' ? 'primary-button' : 'link',
            onClick: () => setActiveHref('/points-system')
          },
          {
            type: 'button',
            text: '家庭日程',
            variant: activeHref === '/schedule' ? 'primary-button' : 'link',
            onClick: () => setActiveHref('/schedule')
          },
          {
            type: 'button',
            text: '家庭设置',
            variant: activeHref === '/test' ? 'primary-button' : 'link',
            onClick: () => setActiveHref('/test')
          },
          {
            type: 'button',
            text: darkMode ? '🌙 暗黑' : '☀️ 明亮',
            onClick: toggleDarkMode
          },
          {
            type: 'menu-dropdown',
            text: currentUser?.name || '用户',
            description: ['Parent', 'parent'].includes(currentUser?.role) ? '家长' : '孩子',
            iconName: 'user-profile',
            items: [
              {
                id: 'profile',
                text: '个人资料'
              },
              {
                id: 'settings',
                text: '设置'
              },
              {
                id: 'logout',
                text: '退出登录',
                onClick: () => {
                  logout();
                  setIsAuthenticated(false);
                  setCurrentUser(null);
                  setAuthError('已退出登录');
                  // 重新初始化应用
                  setTimeout(() => initializeApp(), 1000);
                }
              }
            ]
          }
        ]}
      />
      {isAuthenticated && !authError && !loading ? (
        <div style={{ padding: '16px', margin: '0' }}>
          {/* 根据 activeHref 渲染对应的组件 */}
          {activeHref === '/tasks' && (
            <TaskView 
              familyId={currentUser?.family_id}
              familyName={familyName}
              currentUser={currentUser}
              initialMembers={familyMembers}
              chatrooms={chatrooms}
            />
          )}
          {activeHref === '/task-manager' && (
            <TaskManager 
              onCreateTask={handleCreateTask}
              onUpdateTask={handleUpdateTask}
              onDeleteTask={handleDeleteTask}
              currentUser={currentUser}
            />
          )}
          {activeHref === '/points-system' && (
            <PointsSystem 
              familyMembers={familyMembers}
              onUpdateUser={handleUpdateUser}
              currentUser={currentUser}
            />
          )}
          {activeHref === '/schedule' && (
            <FamilySchedule 
              users={familyMembers}
              currentUser={currentUser}
            />
          )}
          {activeHref === '/test' && (
            <FamilySettingsView 
              currentUser={currentUser}
              onFamilyCreated={handleFamilyCreated}
            />
          )}
        </div>
      ) : (
        renderContent()
      )}
    </div>
  );
}

export default App;